// Le module de collaboration (§8) — panneau latéral ouvert par une pastille en
// bas à droite, disponible PARTOUT, accueil compris. Reprise du geste de
// l'AnomalyFab V2, déjà connu.
//
// Deux objets, deux natures :
//   • le FIL est continu, indépendant du jour ; chaque message porte un
//     contexte facultatif qui le rend cliquable vers le bon endroit ;
//   • une CONSIGNE n'est pas un message, c'est une chose à faire attribuée à
//     une journée. Elle apparaît dans la run de ce jour-là, pour qui que ce
//     soit qui la traite.
//
// Les trois niveaux d'urgence sont PUREMENT VISUELS : une urgence qui déclenche
// des mécanismes finit toujours détournée (§8.3).
//
// Ce qui ne passe PAS par ici : les signalements caissiers, qui restent dans le
// dépouillement, à l'endroit où tu constates le problème.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { addDays, formatMedium, todayIso } from 'sotcp-core/dayUtils'
import * as api from '@/data/api'
import { useAuth } from '@/lib/auth'
import { initiales, timeAgo } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { aller } from '@/lib/router'
import { Chip, Lamp, Notice, Vide } from '@/ui/primitives'
import { useEchap, useToast } from '@/ui/Toast'
import type { Couleur } from '@/ui/primitives'
import type { Consigne, Message } from '@/domain/types'

export interface Contexte { date: string | null; etape: string | null }

const URGENCES: Array<{ n: 1 | 2 | 3; nom: string; c: Couleur }> = [
  { n: 1, nom: 'normal', c: 'neutre' },
  { n: 2, nom: 'important', c: 'warn' },
  { n: 3, nom: 'urgent', c: 'stop' },
]

/** Jours en arrière et en avant couverts par le chargement des consignes. */
const JOURS_AVANT = 13
const JOURS_APRES = 14

const MOT_STATUT: Record<Consigne['statut'], string> = {
  a_faire: 'À FAIRE', faite: 'FAITE', de_cote: 'MISE DE CÔTÉ', reportee: 'REPORTÉE',
}

/** La couleur d'une consigne selon son état — l'urgence ne parle qu'à faire. */
function lampeConsigne(c: Consigne, urgence: Couleur): Couleur {
  if (c.statut === 'faite') return 'ok'
  if (c.statut === 'de_cote') return 'warn'
  if (c.statut === 'reportee') return 'neutre'
  return urgence
}

export default function Pastille({ contexte }: { contexte: Contexte }) {
  const [ouvert, setOuvert] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [consignes, setConsignes] = useState<Consigne[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [onglet, setOnglet] = useState<'fil' | 'consignes'>('fil')

  const seq = useRef(0)
  const vivant = useRef(true)
  // Remis à true à l'exécution, pas seulement à la déclaration : en StrictMode
  // le premier démontage laisserait sinon le panneau muet pour de bon.
  useEffect(() => { vivant.current = true; return () => { vivant.current = false } }, [])

  const charger = useCallback(async () => {
    // Deux chargements peuvent se croiser (ouverture + minuterie + « Rafraîchir ») :
    // seule la réponse à la demande la plus récente a le droit d'écrire.
    const n = ++seq.current
    const aujourdhui = todayIso()
    // La fenêtre va aussi vers l'AVANT : une consigne posée pour la semaine
    // prochaine disparaissait de la liste à la seconde où on la postait.
    const dates = Array.from(
      { length: JOURS_AVANT + JOURS_APRES + 1 },
      (_, i) => addDays(aujourdhui, i - JOURS_AVANT),
    )
    try {
      const [m, c] = await Promise.all([api.chargerMessages(60), api.chargerConsignes(dates)])
      if (!vivant.current || n !== seq.current) return
      setMessages(m); setConsignes(c); setErreur(null)
    } catch (e) {
      // Une erreur avalée transforme un réseau coupé en « fil vide », c'est-à-dire
      // en bonne nouvelle. Elle se dit.
      if (!vivant.current || n !== seq.current) return
      setErreur(messageErreur(e))
    }
  }, [])

  // La pastille reste montée toute la session : chargée une seule fois, le fil
  // et le badge dateraient de l'ouverture de l'application — on ouvrirait le
  // panneau à 7 h pour lire ce que l'équipe du soir a écrit, et on verrait
  // l'écran d'hier. On relit donc à chaque ouverture, au retour sur l'onglet,
  // et toutes les deux minutes tant que l'écran est visible — jamais en fond.
  useEffect(() => { void charger() }, [charger])
  useEffect(() => { if (ouvert) void charger() }, [ouvert, charger])
  useEffect(() => {
    const relire = () => { if (!document.hidden) void charger() }
    const t = window.setInterval(relire, 120_000)
    document.addEventListener('visibilitychange', relire)
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', relire) }
  }, [charger])

  const fermer = useCallback(() => setOuvert(false), [])
  useEchap(ouvert, fermer)

  // Le badge compte ce qui est DÛ : une consigne posée pour jeudi n'est pas un
  // retard mardi, et la faire clignoter deux jours à l'avance apprend à l'ignorer.
  const aujourdhui = todayIso()
  const aTraiter = consignes.filter(
    (c) => c.statut === 'a_faire' && c.journee_cible <= aujourdhui,
  ).length

  return (
    <>
      {/* On clique sur la pastille À CAUSE du badge : si quelque chose est dû,
          le panneau s'ouvre sur les consignes, pas sur le fil — un clic de moins
          debout, et l'écran répond à la question qu'on venait de poser. */}
      <button className="pastille"
              onClick={() => {
                if (!ouvert) setOnglet(aTraiter > 0 ? 'consignes' : 'fil')
                setOuvert(!ouvert)
              }}
              aria-expanded={ouvert} aria-label="Collaboration">
        FIL
        {aTraiter > 0 && <span className="badge">{aTraiter}</span>}
      </button>

      {ouvert && (
        <div className="panneau" role="dialog" aria-label="Collaboration">
          <div className="hd">
            <Lamp c="act" />
            <b style={{ fontSize: 13 }}>Collaboration</b>
            <span className="sp" />
            <button className="btn gh sm" onClick={() => void charger()}>Rafraîchir</button>
            <button className="btn gh sm" onClick={fermer}>Fermer ✕</button>
          </div>
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={onglet === 'fil'} onClick={() => setOnglet('fil')}>
              Le fil
            </button>
            <button role="tab" aria-selected={onglet === 'consignes'} onClick={() => setOnglet('consignes')}>
              Consignes{aTraiter > 0 ? ` · ${aTraiter}` : ''}
            </button>
          </div>

          {erreur && (
            <div style={{ padding: '10px 12px 0' }}>
              <Notice c="stop" action={
                <button className="btn sm" onClick={() => void charger()}>Réessayer</button>
              }>{erreur}</Notice>
            </div>
          )}

          {onglet === 'fil'
            ? <Fil messages={messages} contexte={contexte} recharger={charger} />
            : <Consignes consignes={consignes} contexte={contexte} recharger={charger} />}
        </div>
      )}
    </>
  )
}

function Fil({ messages, contexte, recharger }: {
  messages: Message[]; contexte: Contexte; recharger: () => Promise<void>
}) {
  const { user, nom } = useAuth()
  const toast = useToast()
  const [texte, setTexte] = useState('')
  const [avecContexte, setAvecContexte] = useState(true)
  const [envoi, setEnvoi] = useState(false)
  const champ = useRef<HTMLTextAreaElement>(null)
  const corps = useRef<HTMLDivElement>(null)

  // Le panneau s'ouvre pour écrire neuf fois sur dix : le curseur y est déjà.
  useEffect(() => { champ.current?.focus() }, [])
  // Le fil est chronologique : on l'ouvre sur le dernier message, pas le premier.
  // On déplace la boîte, pas la page — scrollIntoView emmènerait l'écran entier.
  useEffect(() => {
    const el = corps.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages])

  const envoyer = async () => {
    const t = texte.trim()
    if (!t || envoi) return
    setEnvoi(true)
    try {
      await api.posterMessage({
        texte: t, auteurNom: nom,
        journee: avecContexte ? contexte.date : null,
        etape: avecContexte ? contexte.etape : null,
      })
      setTexte(''); await recharger()
    } catch (e) { toast(messageErreur(e), true) }
    finally { setEnvoi(false) }
  }

  return (
    <>
      <div className="body" ref={corps}>
        {messages.length === 0
          ? <Vide>Le fil est vide.</Vide>
          : messages.map((m) => (
            <div className="msg" key={m.id}>
              <div className="who">
                <span className={`av${m.auteur === user?.id ? ' b' : ''}`}>{initiales(m.auteur_nom)}</span>
                <span className="nm">{m.auteur_nom ?? '—'}</span>
                <span className="tm">{timeAgo(m.at)}</span>
              </div>
              <div className="tx">{m.texte}</div>
              {m.journee_date && (
                <button className="btn gh sm" style={{ marginTop: 5 }}
                        onClick={() => aller({ vue: 'run', date: m.journee_date!, etape: m.etape_code })}>
                  Ouvrir {formatMedium(m.journee_date)}{m.etape_code ? ` · étape ${m.etape_code}` : ''} →
                </button>
              )}
            </div>
          ))}
      </div>
      <div className="foot">
        <textarea ref={champ} placeholder="Écrire au fil…" value={texte}
                  onChange={(e) => setTexte(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void envoyer() }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          {contexte.date && (
            <label className="hint" style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="checkbox" checked={avecContexte} onChange={(e) => setAvecContexte(e.target.checked)} />
              Rattacher à {contexte.date}{contexte.etape ? ` · ${contexte.etape}` : ''}
            </label>
          )}
          <span className="sp" />
          <button className="btn pri sm" data-encours={envoi ? 'true' : undefined}
                  disabled={texte.trim() === ''} onClick={() => void envoyer()}>
            Envoyer au fil
          </button>
        </div>
        <div className="hint" style={{ marginTop: 6 }}>
          Ctrl+Entrée pour envoyer · pièces jointes (10 Mo, bucket{' '}
          <span className="mo">fc-pieces</span>) : phase 4.
        </div>
      </div>
    </>
  )
}

function Consignes({ consignes, contexte, recharger }: {
  consignes: Consigne[]; contexte: Contexte; recharger: () => Promise<void>
}) {
  const { nom } = useAuth()
  const toast = useToast()
  const aujourdhui = todayIso()
  const [texte, setTexte] = useState('')
  const [urgence, setUrgence] = useState<1 | 2 | 3>(1)
  const [cible, setCible] = useState(contexte.date ?? aujourdhui)
  const [surEtape, setSurEtape] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  const [occupee, setOccupee] = useState<string | null>(null)
  const champ = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { champ.current?.focus() }, [])

  // Ce qui est dû d'abord, du plus vieux au plus récent : la consigne en retard
  // depuis trois jours ne doit pas se retrouver sous celle de demain.
  const triees = useMemo(() => {
    const rang = (c: Consigne) => (c.statut === 'a_faire' ? 0 : c.statut === 'reportee' ? 1 : 2)
    return [...consignes].sort((a, b) =>
      rang(a) - rang(b)
      || a.journee_cible.localeCompare(b.journee_cible)
      || b.urgence - a.urgence)
  }, [consignes])

  // Le champ date peut être VIDÉ : sans cette garde, on tape la consigne, on
  // poste, et le serveur renvoie une erreur cryptique sur une journée nulle.
  // Et au-delà de la fenêtre chargée, la consigne part bien mais ne se relit
  // pas ici — on le dit plutôt que de la laisser paraître perdue.
  const horsFenetre = cible !== ''
    && (cible < addDays(aujourdhui, -JOURS_AVANT) || cible > addDays(aujourdhui, JOURS_APRES))

  const creer = async () => {
    const t = texte.trim()
    if (!t || !cible || envoi) return
    setEnvoi(true)
    try {
      await api.creerConsigne({
        journee: cible, etape: surEtape ? contexte.etape : null,
        texte: t, urgence, auteurNom: nom,
      })
      setTexte(''); await recharger()
      toast('CONSIGNE POSTÉE')
    } catch (e) { toast(messageErreur(e), true) }
    finally { setEnvoi(false) }
  }

  const agir = async (c: Consigne, statut: Consigne['statut']) => {
    if (occupee) return
    setOccupee(c.id)
    try {
      if (statut === 'reportee') {
        // « Reporter à demain » disait la vérité pour une consigne du jour et
        // mentait pour toutes les autres : cible + 1 sur une consigne vieille de
        // trois jours la reposait avant-hier, donc toujours en retard, donc
        // apparemment sans effet. Demain, c'est demain.
        const demain = addDays(aujourdhui, 1)
        const suivant = addDays(c.journee_cible, 1)
        await api.reporterConsigne(c.id, suivant > demain ? suivant : demain)
      } else {
        await api.majConsigne(c.id, statut, nom)
      }
      await recharger()
    } catch (e) { toast(messageErreur(e), true) }
    finally { setOccupee(null) }
  }

  return (
    <>
      <div className="body">
        {triees.length === 0
          ? <Vide>Aucune consigne.</Vide>
          : triees.map((c) => {
            const u = URGENCES.find((x) => x.n === c.urgence) ?? URGENCES[0]!
            const enRetard = c.statut === 'a_faire' && c.journee_cible < aujourdhui
            const aVenir = c.journee_cible > aujourdhui
            const bloque = occupee === c.id ? 'true' : undefined
            return (
              <div className="li" key={c.id}>
                <Lamp c={lampeConsigne(c, u.c)} />
                <div className="body">
                  <div className="t" style={c.statut === 'faite'
                    ? { textDecoration: 'line-through', color: 'var(--ink4)' } : undefined}>
                    {c.texte}
                  </div>
                  <div className="s">
                    {formatMedium(c.journee_cible).toUpperCase()}
                    {c.etape_cible ? ` · ÉTAPE ${c.etape_cible}` : ''} ·
                    {' '}DE {(c.auteur_nom ?? '—').toUpperCase()}
                  </div>
                  <div style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {c.urgence > 1 && <Chip c={u.c}>{u.nom.toUpperCase()}</Chip>}
                    {enRetard && <Chip c="stop">EN RETARD</Chip>}
                    {aVenir && c.statut === 'a_faire' && <Chip>À VENIR</Chip>}
                  </div>

                  {c.statut === 'a_faire' ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      {/* solder · différer · repousser — trois tons, trois sens */}
                      <button className="btn ok sm" data-encours={bloque}
                              onClick={() => void agir(c, 'faite')}>Marquer faite</button>
                      <button className="btn warn sm" data-encours={bloque}
                              onClick={() => void agir(c, 'de_cote')}>Mettre de côté</button>
                      <button className="btn gh sm" data-encours={bloque}
                              onClick={() => void agir(c, 'reportee')}>Reporter à demain</button>
                    </div>
                  ) : (
                    <>
                      <div className="s">
                        {MOT_STATUT[c.statut]}
                        {c.fait_par_nom ? ` PAR ${c.fait_par_nom.toUpperCase()}` : ''}
                      </div>
                      {/* Une consigne soldée par erreur doit pouvoir revenir :
                          sinon le panneau est un aller simple. */}
                      <div style={{ marginTop: 6 }}>
                        <button className="btn gh sm" data-encours={bloque}
                                onClick={() => void agir(c, 'a_faire')}>Remettre à faire</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          })}
      </div>
      <div className="foot">
        <textarea ref={champ} placeholder="Une chose à faire, attribuée à une journée…" value={texte}
                  onChange={(e) => setTexte(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void creer() }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="fld" style={{ width: 130 }} type="date" value={cible}
                 aria-label="Journée visée par la consigne"
                 onChange={(e) => setCible(e.target.value)} />
          {/* Trois états, un seul retenu : aria-pressed, pas une liste déroulante. */}
          <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Urgence">
            {URGENCES.map((u) => (
              <button key={u.n}
                      className={`btn${u.c === 'neutre' ? '' : ` ${u.c}`} sm`}
                      aria-pressed={urgence === u.n}
                      onClick={() => setUrgence(u.n)}>
                {u.nom}
              </button>
            ))}
          </div>
          {contexte.etape && (
            <label className="hint" style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="checkbox" checked={surEtape} onChange={(e) => setSurEtape(e.target.checked)} />
              Étape {contexte.etape}
            </label>
          )}
          <span className="sp" />
          <button className="btn pri sm" data-encours={envoi ? 'true' : undefined}
                  disabled={texte.trim() === '' || cible === ''} onClick={() => void creer()}>
            Poster la consigne
          </button>
        </div>
        {cible === '' && (
          <div className="hint" style={{ marginTop: 6, color: 'var(--warn)' }}>
            Choisis la journée visée : une consigne sans journée n’a nulle part où apparaître.
          </div>
        )}
        {horsFenetre && (
          <div className="hint" style={{ marginTop: 6, color: 'var(--warn)' }}>
            Le {formatMedium(cible)} est hors de la fenêtre de ce panneau : la consigne partira,
            mais elle ne se relira ici qu’à l’approche de cette journée.
          </div>
        )}
        <div className="hint" style={{ marginTop: 6 }}>
          Ctrl+Entrée pour poster · l’urgence est purement visuelle : elle ne déclenche rien.
        </div>
      </div>
    </>
  )
}
